import React, { useMemo } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Container, Card, Alert, Table, Button } from "react-bootstrap";
import { Spin } from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";

import PageTitle from "@/common/PageTitle/PageTitle";
import {
  useCanViewPage,
  useIsSuperAdmin,
} from "@/contexts/SuperAdminIdentityContext";
import {
  useGetTrackSongHistoryDetailQuery,
  extractTracksErrorMessage,
  type SongHistoryTimelineEvent,
  type SongHistoryDetailResponse,
} from "@/features/tracks/tracksApi";
import { formatDateTime } from "@/features/processing/processingDisplay";
import { useUploaderDirectory } from "@/features/processing/uploaderDisplay";
import {
  renderActivityBadge,
  renderOutcomeBadge,
  renderLifecycleStatusBadge,
} from "@/features/tracks/songHistoryDisplay";
import {
  formatTimelineActor,
  formatTimelineDetailLines,
  timelineHasTechnicalPayload,
} from "@/features/tracks/songHistoryTimelineDisplay";
import { ScheduleSideBadge } from "@/features/channels/scheduleSideDisplay";
import { formatEntityRef } from "@/lib/entityRef";
import { EntityRefBadge } from "@/components/EntityRef/EntityRefBadge";
import styles from "./uploader-history.module.scss";

const PAGE_ID = "uploader-history";

function TimelineEventBody(props: {
  event: SongHistoryTimelineEvent;
  summary: SongHistoryDetailResponse["summary"];
  resolveActor: (
    sub?: string | null,
    employeeId?: string | null,
  ) => { label: string; resolved: boolean };
  canViewTechnical: boolean;
  jobPath: string | undefined;
  onViewJob: (path: string) => void;
}) {
  const { event, summary, resolveActor, canViewTechnical, jobPath, onViewJob } =
    props;
  const actor = formatTimelineActor(
    event.actorSub,
    event.actorEmployeeId,
    resolveActor,
  );
  const detailLines = formatTimelineDetailLines(
    event,
    summary,
    actor?.label || null,
  );
  const showTechnical =
    canViewTechnical && timelineHasTechnicalPayload(event);

  return (
    <div className={styles.timelineBody}>
      <div className={styles.timelineLabel}>{event.label}</div>
      {actor ? (
        <div className={styles.timelineMeta}>{actor.label}</div>
      ) : null}
      {detailLines.length > 0 ? (
        <dl className={styles.timelineDetails}>
          {detailLines.map((line) => (
            <div key={`${line.label}-${line.value}`} className={styles.timelineDetailRow}>
              <dt>{line.label}</dt>
              <dd>{line.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {event.auditEventId ? (
        <div className={styles.timelineAuditRef}>
          Audit {event.auditEventId}
        </div>
      ) : null}
      {showTechnical ? (
        <details className={styles.technicalDetails}>
          <summary>Technical Details</summary>
          <pre className={styles.diffBlock} aria-label="Raw audit before and after payload">
            {JSON.stringify(
              { before: event.before, after: event.after },
              null,
              2,
            )}
          </pre>
        </details>
      ) : null}
      {jobPath ? (
        <Button
          variant="link"
          size="sm"
          className={styles.detailLink}
          onClick={() => onViewJob(jobPath)}
        >
          View Job
        </Button>
      ) : null}
    </div>
  );
}

const UploaderHistoryDetail: React.FC = () => {
  const navigate = useNavigate();
  const { trackId: rawTrackId } = useParams<{ trackId: string }>();
  const trackId = rawTrackId ? decodeURIComponent(rawTrackId) : "";
  const canView = useCanViewPage(PAGE_ID);
  const canViewTechnical = useIsSuperAdmin();
  const uploaderDirectory = useUploaderDirectory();
  const shortRef = trackId ? formatEntityRef("track", trackId) : "—";

  const detailQuery = useGetTrackSongHistoryDetailQuery(trackId, {
    skip: !canView || !trackId,
  });

  const detail = detailQuery.data;
  const availableJobPaths = useMemo(() => {
    const map = new Map<string, string>();
    for (const attempt of detail?.attempts || []) {
      if (attempt.jobId && attempt.viewJobPath) {
        map.set(attempt.jobId, attempt.viewJobPath);
      }
    }
    for (const row of detail?.playbackHistory || []) {
      if (row.sourceProcessingJobId && row.viewJobPath) {
        map.set(row.sourceProcessingJobId, row.viewJobPath);
      }
    }
    return map;
  }, [detail]);

  const loading = detailQuery.isLoading;
  const fetching = detailQuery.isFetching;

  if (!canView) {
    return (
      <Container fluid>
        <Alert variant="warning">
          You do not have permission to view Song History.
        </Alert>
      </Container>
    );
  }

  if (!trackId) {
    return (
      <Container fluid>
        <Alert variant="danger">Missing Track ID.</Alert>
      </Container>
    );
  }

  const hardError = !detail && !loading && detailQuery.isError;

  const originalUploader = detail
    ? uploaderDirectory.resolve(
        detail.summary.originalUploaderSub,
        detail.summary.originalUploaderEmployeeId,
      )
    : null;

  return (
    <Container fluid>
      <Card className={styles.card}>
        <Card.Body>
          <Card.Title className={styles.cardTitle}>
            <PageTitle title="Song History Detail" />
            <div className={styles.titleActions}>
              <Link to="/uploader-history" className={styles.viewAllLink}>
                ← Back to Song History
              </Link>
              {detail?.summary.openSongPath ? (
                <Link
                  to={detail.summary.openSongPath}
                  className={styles.viewAllLink}
                >
                  Open Song
                </Link>
              ) : null}
            </div>
          </Card.Title>

          <p className={styles.detailMeta}>
            Permanent record for Track {shortRef}
            {" · "}
            <code>{trackId}</code>
          </p>

          {hardError ? (
            <Alert variant="danger">
              {extractTracksErrorMessage(detailQuery.error) ||
                "Failed to load Song History Detail."}
            </Alert>
          ) : (
            <Spin spinning={loading || fetching}>
              {!detail ? (
                <Alert variant="warning" className="mb-0">
                  No Song History record found for this Track.
                </Alert>
              ) : (
                <>
                  {/* 1. Song summary header */}
                  <section className={styles.detailSection}>
                    <div className={styles.sectionTitleRow}>
                      <h2 className={styles.sectionTitle}>Song</h2>
                      <div className={styles.lifecycleStatusBadge}>
                        {renderLifecycleStatusBadge(detail.summary.status)}
                      </div>
                    </div>
                    <div className={styles.summaryGrid}>
                      <div>
                        <div className={styles.summaryLabel}>Title</div>
                        <div className={styles.summaryValue}>
                          {detail.summary.title || "—"}
                        </div>
                      </div>
                      <div>
                        <div className={styles.summaryLabel}>Artist</div>
                        <div className={styles.summaryValue}>
                          {detail.summary.artistName || "—"}
                        </div>
                      </div>
                      <div>
                        <div className={styles.summaryLabel}>Album</div>
                        <div className={styles.summaryValue}>
                          {detail.summary.albumTitle || "—"}
                        </div>
                      </div>
                      <div>
                        <div className={styles.summaryLabel}>Record Label</div>
                        <div className={styles.summaryValue}>
                          {detail.summary.recordLabelName || "—"}
                        </div>
                      </div>
                      <div>
                        <div className={styles.summaryLabel}>Track ID</div>
                        <div className={styles.summaryValue}>
                          <EntityRefBadge kind="track" id={detail.summary.trackId} />
                        </div>
                      </div>
                      <div>
                        <div className={styles.summaryLabel}>
                          Original Uploader
                        </div>
                        <div className={styles.summaryValue}>
                          {originalUploader?.label || "—"}
                        </div>
                      </div>
                      <div>
                        <div className={styles.summaryLabel}>First Uploaded</div>
                        <div className={styles.summaryValue}>
                          {formatDateTime(detail.summary.firstUploadedAt)}
                        </div>
                      </div>
                      <div>
                        <div className={styles.summaryLabel}>
                          Current Outcome
                        </div>
                        <div className={styles.summaryValue}>
                          {renderOutcomeBadge(
                            detail.summary.currentOutcome,
                            detail.summary.currentOutcomeLabel,
                          )}
                        </div>
                      </div>
                      <div>
                        <div className={styles.summaryLabel}>
                          Current Playback Version
                        </div>
                        <div className={styles.summaryValue}>
                          {detail.summary.versionsDisplay || "—"}
                        </div>
                      </div>
                      <div>
                        <div className={styles.summaryLabel}>
                          Latest Activity
                        </div>
                        <div className={styles.summaryValue}>
                          {renderActivityBadge(
                            detail.summary.latestActivityType,
                            detail.summary.latestActivityLabel,
                          )}
                          <div className={styles.activityDate}>
                            {formatDateTime(detail.summary.latestActivityAt)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* 2. Song lifecycle */}
                  <section className={styles.detailSection}>
                    <h2 className={styles.sectionTitle}>Song Lifecycle</h2>
                    <p className={styles.sectionHelp}>
                      What has happened to this Song since it was first
                      uploaded. Read-only.
                    </p>
                    {detail.timeline.length === 0 ? (
                      <p className={styles.muted}>No timeline events yet.</p>
                    ) : (
                      <ul className={styles.timelineList}>
                        {detail.timeline.map((event, index) => (
                          <li
                            key={`${event.type}-${event.at}-${event.jobId || index}`}
                            className={styles.timelineItem}
                          >
                            <div className={styles.timelineWhen}>
                              {formatDateTime(event.at)}
                            </div>
                            <TimelineEventBody
                              event={event}
                              summary={detail.summary}
                              resolveActor={uploaderDirectory.resolve}
                              canViewTechnical={canViewTechnical}
                              jobPath={
                                event.jobId
                                  ? availableJobPaths.get(event.jobId)
                                  : undefined
                              }
                              onViewJob={(path) => navigate(path)}
                            />
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>

                  {/* 3. Processing attempts */}
                  <section className={styles.detailSection}>
                    <h2 className={styles.sectionTitle}>
                      Processing attempts
                    </h2>
                    <Table
                      bordered
                      hover
                      responsive
                      className={`legacy-row-height-table ${styles.historyTable}`}
                    >
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Purpose</th>
                          <th>Outcome</th>
                          <th>When</th>
                          <th>View Job</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.attempts.length === 0 ? (
                          <tr>
                            <td colSpan={5} className={styles.emptyCell}>
                              No processing attempts on record.
                            </td>
                          </tr>
                        ) : (
                          detail.attempts.map((attempt) => (
                            <tr key={attempt.jobId}>
                              <td>{attempt.attemptNumber}</td>
                              <td>{attempt.purposeLabel}</td>
                              <td>{attempt.outcome}</td>
                              <td className={styles.dateCell}>
                                {formatDateTime(attempt.at)}
                              </td>
                              <td>
                                {attempt.viewJobPath ? (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className={styles.detailLink}
                                    onClick={() =>
                                      navigate(attempt.viewJobPath as string)
                                    }
                                  >
                                    View Job
                                  </Button>
                                ) : (
                                  <span className={styles.timelineMeta}>
                                    {attempt.jobRecordMessage || "—"}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </section>

                  {/* 4. Playback history */}
                  <section className={styles.detailSection}>
                    <h2 className={styles.sectionTitle}>Playback history</h2>
                    <p className={styles.sectionHelp}>
                      Approved playback versions for this Song. Read-only — no
                      revoke.
                    </p>
                    <Table
                      bordered
                      hover
                      responsive
                      className={`legacy-row-height-table ${styles.historyTable}`}
                    >
                      <thead>
                        <tr>
                          <th>Version</th>
                          <th>Event</th>
                          <th>Status</th>
                          <th>When</th>
                          <th>View Job</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.playbackHistory.length === 0 ? (
                          <tr>
                            <td colSpan={5} className={styles.emptyCell}>
                              No playback versions on record.
                            </td>
                          </tr>
                        ) : (
                          detail.playbackHistory.map((row) => (
                            <tr key={row.artifactVersionId}>
                              <td className={styles.versionsCell}>
                                {row.versionsDisplay || "—"}
                                {row.isCurrent ? (
                                  <span className={styles.currentPill}>
                                    {" "}
                                    current
                                  </span>
                                ) : null}
                              </td>
                              <td>{row.eventLabel}</td>
                              <td>{row.status || "—"}</td>
                              <td className={styles.dateCell}>
                                {formatDateTime(row.createdAt)}
                              </td>
                              <td>
                                {row.viewJobPath ? (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className={styles.detailLink}
                                    onClick={() =>
                                      navigate(row.viewJobPath as string)
                                    }
                                  >
                                    View Job
                                  </Button>
                                ) : (
                                  <span className={styles.timelineMeta}>
                                    {row.jobRecordMessage || "—"}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </section>

                  {/* 5. Channel Membership History */}
                  <section className={styles.detailSection}>
                    <h2 className={styles.sectionTitle}>
                      Channel Membership History
                    </h2>
                    <p className={styles.sectionHelp}>
                      Channel Programming placements for this Song. Active songs
                      show current placements; Archived and Deleted songs show
                      the frozen snapshot taken before removal. Schedule Side
                      shows whether each placement was Staging, Pending Update,
                      or Production. Read-only.
                    </p>
                    <Table
                      bordered
                      hover
                      responsive
                      className={`legacy-row-height-table ${styles.historyTable}`}
                    >
                      <thead>
                        <tr>
                          <th>Schedule</th>
                          <th>Channel</th>
                          <th>Rotation</th>
                          <th>Removed</th>
                          <th>Operation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!detail.channelMembershipHistory ||
                        detail.channelMembershipHistory.length === 0 ? (
                          <tr>
                            <td colSpan={5} className={styles.emptyCell}>
                              No channel placements on record.
                            </td>
                          </tr>
                        ) : (
                          detail.channelMembershipHistory.map((row, index) => (
                            <tr
                              key={`${row.rotationTrackId || row.rotationId || "row"}-${index}`}
                            >
                              <td>
                                {row.scheduleSide ? (
                                  <ScheduleSideBadge side={row.scheduleSide} />
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td>{row.channelName || row.channelId || "—"}</td>
                              <td>
                                {row.rotationName ||
                                  row.category ||
                                  row.rotationId ||
                                  "—"}
                                {row.position != null ? (
                                  <div className={styles.timelineMeta}>
                                    Position {row.position}
                                  </div>
                                ) : null}
                              </td>
                              <td className={styles.dateCell}>
                                {row.removedAt
                                  ? formatDateTime(row.removedAt)
                                  : "—"}
                              </td>
                              <td>{row.operation || "—"}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </section>

                  {/* 6. Tombstone — Deleted only */}
                  {detail.tombstone ? (
                    <section className={styles.detailSection}>
                      <h2 className={styles.sectionTitle}>Tombstone</h2>
                      <Alert variant="secondary" className="mb-0">
                        Permanently deleted{" "}
                        {formatDateTime(detail.tombstone.permanentlyDeletedAt)}
                        {detail.tombstone.permanentDeleteReason
                          ? ` · ${detail.tombstone.permanentDeleteReason}`
                          : null}
                        {detail.tombstone.deletionManifestId ? (
                          <>
                            {" · "}
                            <code>{detail.tombstone.deletionManifestId}</code>
                          </>
                        ) : null}
                      </Alert>
                    </section>
                  ) : null}
                </>
              )}
            </Spin>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default withAuthenticationRequired(UploaderHistoryDetail);
